import React, {
	type ReactNode,
	useContext,
	// eslint-disable-next-line jira/restricted/react-suspense
	Suspense,
	type ReactElement,
	type PropsWithChildren,
} from 'react';

import { useOwnershipMarkerComponent } from '@jira/platform__ownership-marker/src/ui/useOwnershipMarkerComponent.tsx';
import UFOInteractionContext from '@atlaskit/react-ufo/interaction-context';
import { fg } from '@atlassian/jira-feature-gating';

import useLayoutEffectSAFE from '@atlassian/jira-use-layout-effect-safe';
import { type LazySuspenseProps, LazySuspense, WaitContext } from '@atlassian/react-loosely-lazy';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { useIsHydrateRenderMode } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import { SuspensePlaceholder } from './SuspensePlaceholder.tsx';

function Hold({ name }: { name?: string }): null {
	const interactionContext = useContext(UFOInteractionContext);
	const waitContext = useContext(WaitContext);
	const nameWithUnderscores = fg('jfp_fix_placeholder_name') ? name?.replaceAll('/', '_') : name;

	// Runs synchronously, to allow us to identify async items
	useLayoutEffectSAFE(() => {
		// if until context exists means there is a LazyWait parent component
		// we use that value
		const shouldHold = waitContext ? !!waitContext.currentValue() : true;
		if (interactionContext != null && shouldHold) {
			return interactionContext.hold(nameWithUnderscores);
		}
	}, [interactionContext, nameWithUnderscores, waitContext]);
	return null;
}

/**
 * This is a "safe-to-use" Placeholder but it's actually expected to be used only around React-Loosely-Lazy components.
 * WARNING: Make sure we use same placeholders in a rendering tree.
 * Mixed use may cause unexpected behavior, such as rendering child component twice.
 * Ref: https://hello.atlassian.net/wiki/x/iJpOHwE
 */
function RLLPlaceholder({
	name,
	children,
	fallback,
}: {
	/**
	 * Named Placeholders will help when analysing the break-down of the page load in Performance Portal.
	 * */
	name: string;
	children?: LazySuspenseProps['children'];
	fallback?: LazySuspenseProps['fallback'];
}): ReactElement {
	// react-18: can use fallback directly
	const fb = fallback != null ? fallback : null;
	// react-18: can use children directly
	const kids = children != null ? children : null;
	const OwnershipMarker = useOwnershipMarkerComponent({ markerType: 'placeholder', name });

	return (
		<OwnershipMarker>
			<LazySuspense
				fallback={
					<>
						{fb}
						<Hold name={name} />
					</>
				}
				name={name}
			>
				{kids}
			</LazySuspense>
		</OwnershipMarker>
	);
}

function PlaceholderWithNativeSuspenseOld({
	name,
	children,
	fallback,
}: {
	/**
	 * Named Placeholders will help when analysing the break-down of the page load in Performance Portal.
	 * */
	name: string;
	children?: LazySuspenseProps['children'];
	fallback?: LazySuspenseProps['fallback'];
}): ReactElement {
	// react-18: can use fallback directly
	const fb = fallback != null ? fallback : null;
	// react-18: can use children directly
	const kids = children != null ? children : null;
	const OwnershipMarker = useOwnershipMarkerComponent({ markerType: 'placeholder', name });

	return (
		<OwnershipMarker>
			<Suspense
				fallback={
					<>
						{fb}
						<Hold name={name} />
					</>
				}
			>
				{kids}
			</Suspense>
		</OwnershipMarker>
	);
}

const Placeholder = componentWithCondition(
	// this condition is coupled with hydration rollout, not the fact of hydration
	// SuspensePlaceholder can be used for non-hydrating pages as well, if no "SSR content preservation" is required
	// See PlaceholderWithNativeSuspense
	() => useIsHydrateRenderMode() && fg('jfp_magma_jira-hydrating-placeholder'),
	SuspensePlaceholder,
	RLLPlaceholder,
);
export default Placeholder;

export const PlaceholderWithNativeSuspense = componentWithCondition(
	() => fg('jfp_magma_jira-hydrating-placeholder'),
	SuspensePlaceholder,
	PlaceholderWithNativeSuspenseOld,
);

/**
 * @deprecated - this is now a "new" placeholder, it's just "small" placeholder
 * This is used specifically for R18 concurrent mode to avoid performance regressions.
 * See: https://hello.atlassian.net/wiki/spaces/JDP/pages/4469891701/Investigate+Concurrent+Mode+Optimisation+for+Navigation
 *
 * @see {@link Placeholder} for general "cannot be wrong" cases
 * @see {@link PlaceholderWithNativeSuspense} to be used for "simple" cases without strong SSR support
 */
export const PlaceholderNew = componentWithCondition(
	() => fg('concurrent-native-suspense'),
	PlaceholderWithNativeSuspense,
	Placeholder,
);

/**
 * @deprecated - migration is done use {@link PlaceholderWithNativeSuspense} instead
 */
export function SuspenseMigrationPlaceholder(
	props: PropsWithChildren<{
		name: string;
		condition: true;
		fallback: ReactNode;
	}>,
): ReactElement;
/**
 * A helper component migrate from native Suspense to Placeholder
 * @param condition - determine to use Placeholder(true) or native Suspense(false)
 */
export function SuspenseMigrationPlaceholder(
	props: PropsWithChildren<{
		name: string;
		condition: boolean;
		fallback: ReactNode;
	}>,
): ReactElement;

export function SuspenseMigrationPlaceholder({
	condition,
	fallback,
	name,
	children,
}: PropsWithChildren<{
	name: string;
	condition: boolean;
	fallback: ReactNode;
}>): ReactElement {
	if (condition) {
		return (
			<PlaceholderWithNativeSuspense fallback={fallback} name={name}>
				{children}
			</PlaceholderWithNativeSuspense>
		);
	}
	return <Suspense fallback={fallback}>{children}</Suspense>;
}
